import React, { Component } from 'react';
import { Table, Dropdown, Menu, Row, Col, Button, Tooltip } from 'antd';
import moment from 'moment';
import { ApiHOC, MessageHOC } from '../../';
import {
  SearchOutlined,
  DownloadOutlined,
  CalendarOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import {
  trim,
  toLower,
  isArray,
  uniq,
  uniqBy,
  concat,
  isEqual,
  isPlainObject,
} from 'lodash';
import {
  SORT_TYPE,
  FILTER_TYPE,
} from '../../../constants/ApplicationConstants';
import {
  fetchCurrentCropSalesYearFromApi,
  isCurrentCropSalesYear,
} from '../../../utilities/UtilityFunctions';
import TextSearch from './TextSearch';
import DateSearch from './DateSearch';
import { makeSelectLocale } from '../../../language-provider/selectors';
import './custom-table.less';

class CustomTable extends Component {
  constructor(props) {
    super(props);
    this.state = this.getState();
  }

  componentDidMount() {
    const formColumns = this.formColumns(this.props);
    this.setState({ formColumns });
    fetchCurrentCropSalesYearFromApi(
      this.props.GET,
      (year) => this.setState({ currentCropSalesYear: year }),
      () => this.setState({ currentCropSalesYear: null })
    );
  }

  componentWillReceiveProps(newProps) {
    const formColumns = this.formColumns(newProps);
    this.setState({ formColumns });
  }

  componentDidUpdate(prevProps) {
    const { data, columns, onRowSelect } = prevProps;
    const haveTableRecordsChanged = !isEqual(data, this.props.data);
    const haveTableColumnsChanged = !isEqual(columns, this.props.columns);
    if (haveTableRecordsChanged) {
      this.setState({
        ...(onRowSelect && { selectedRowKeys: this.getInitialSelRows() }),
        ...(haveTableColumnsChanged && { formColumns: this.formColumns() }),
      });
    }
  }

  isFilteredTable = () => {
    const { searchText } = this.state;
    return !Object.keys(searchText || {}).every(
      (key) => !trim(searchText[key])
    );
  };

  getState = () => {
    const { onRowSelect, filterEnabled, isCSVExport } = this.props;
    let result = {};
    //if row selection needed
    if (onRowSelect) {
      result.selectedRowKeys = this.getInitialSelRows();
    }
    if (filterEnabled) {
      result.searchText = {};
      result.filteredData = [];
    }
    //headers if csv export is required
    if (isCSVExport) {
      result.headers = [];
    }
    result.currentCropSalesYear = null;
    return result;
  };

  getInitialSelRows = () => {
    const { data, initRowSelection = [] } = this.props;
    if (isArray(initRowSelection)) {
      return initRowSelection;
    }
    return (data || []).reduce((selectedKeys, record) => {
      if (initRowSelection.indexOf(record.key) > -1) {
        selectedKeys.push(record.key);
      }
      return selectedKeys;
    }, []);
  };

  sortByColumn = (key, sortType, record1, record2) => {
    let val1, val2;
    if (isPlainObject(record1[key])) {
      val1 = toLower(trim(record1[key].value));
      val2 = toLower(trim(record2[key].value));
    } else {
      val1 = toLower(trim(record1[key]));
      val2 = toLower(trim(record2[key]));
    }
    switch (sortType) {
      case SORT_TYPE.NUMBER:
        return Number(val1) - Number(val2);
      case SORT_TYPE.DATE:
        return moment(val2).unix() - moment(val1).unix();
      default:
        const { locale } = this.props;
        return new Intl.Collator(locale).compare(val1, val2);
    }
  };

  formColumns = (dataValue) => {
    const { columns, data, commonMessages, colNameToSort } =
      dataValue === undefined ? this.props : dataValue;

    let resColumns = [];
    let headers = [];
    (columns || []).forEach((col) => {
      let resultCol = {};
      !col.excludeCSV &&
        headers.push({
          label: col['title'],
          key: col['exportKey'] || col['dataIndex'],
        });
      Object.keys(col).forEach((key) => {
        switch (key) {
          case 'dataIndex':
          case 'title':
          case 'render':
          case 'fixed':
          case 'align':
          case 'responsive':
          case 'className':
          case 'width':
          case 'showSorterTooltip':
            resultCol[key] = col[key];
            break;
          case 'sort':
            const sortFunction = col['sortFunc'] || this.sortByColumn;

            if (colNameToSort) {
              if (colNameToSort === col.key) {
                resultCol.defaultSortOrder = col['sortOrd'] || 'ascend';
                if (col.sortOrder) resultCol.sortOrder = col.sortOrder;
              }
              resultCol.sorter = col.sorter;
            } else {
              resultCol.sorter = sortFunction.bind(
                this,
                col.dataIndex,
                col[key]
              );
              resultCol.defaultSortOrder = col['sortOrd'] || 'ascend';
            }
            break;
          case 'filter':
            resultCol.onFilter = (val, record) => {
              let numberWithoutCommas = val;
              if (val !== record?.application_id) {
                numberWithoutCommas = Number(val.replaceAll(/[^0-9.]+/g, ''));
              }
              const searchVal = toLower(
                trim(String(numberWithoutCommas || val))
              );
              if (val) {
                if (!this.state.searchText[col.dataIndex]) {
                  this.updateSearchKey(col.dataIndex, searchVal);
                }
              } else {
                this.updateSearchKey(col.dataIndex, searchVal);
              }
              const value = record[col.dataIndex];
              if (col[key] === FILTER_TYPE.SELECT) {
                return val === value;
              }
              if (isArray(value)) {
                if ((value || []).length > 0) {
                  const isObj = isPlainObject(value[0]);
                  if (isObj) {
                    return (
                      (value || []).filter(
                        (op) => toLower(trim(String(op.key))) === searchVal
                      ).length > 0
                    );
                  }
                  return (value || []).indexOf(searchVal) > -1;
                }
              } else {
                const isObj = isPlainObject(value);
                const recordVal = isObj ? (value || {}).key : value;
                if (isObj) {
                  return toLower(trim(String(recordVal))) === searchVal;
                }
                return toLower(trim(String(recordVal))).indexOf(searchVal) > -1;
              }
            };
            switch (col[key]) {
              case FILTER_TYPE.SELECT:
                let colValues = [];
                let isObj = false;
                let isArr = false;
                (data || []).forEach((rec) => {
                  let val = rec[col.dataIndex];
                  if (0 === val || val) {
                    isArr = isArray(val);
                    if (isArr) {
                      if ((val || []).length > 0) {
                        isObj = isPlainObject(val[0]);
                      }
                    } else {
                      isObj = isPlainObject(val);
                    }
                    colValues = concat(colValues, val);
                  }
                });
                if (isObj) {
                  resultCol.filters = uniqBy(colValues, 'key')
                    .filter((f) => trim(String(f.key)))
                    .map((filter) => ({
                      text: filter.value,
                      value: filter.key,
                    }));
                } else {
                  resultCol.filters = uniq(colValues)
                    .filter((f) => trim(String(f)))
                    .map((filter) => ({
                      text: filter,
                      value: filter,
                    }));
                }
                break;
              case FILTER_TYPE.TEXT:
                resultCol.filterDropdown = (options) => (
                  <TextSearch
                    {...options}
                    handleSearch={this.handleSearch.bind(this, col.dataIndex)}
                    handleReset={this.handleReset.bind(this, col.dataIndex)}
                    commonMessages={commonMessages}
                  />
                );
                resultCol.filterIcon = (filtered) => (
                  <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                  />
                );

                break;
              case FILTER_TYPE.DATE:
                resultCol.filterDropdown = (options) => (
                  <DateSearch
                    {...options}
                    handleSearch={this.handleSearch.bind(this, col.dataIndex)}
                    handleReset={this.handleReset.bind(this, col.dataIndex)}
                  />
                );
                resultCol.filterIcon = (filtered) => (
                  <CalendarOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                  />
                );
                resultCol.onFilter = (val, record) => {
                  const startDate = val[0];
                  const endDate = val[1];
                  const value = record[col.dataIndex];
                  return (
                    startDate.isSameOrBefore(value, 'day') &&
                    endDate.isSameOrAfter(value, 'day')
                  );
                };

                break;
              default:
            }
            break;
          default:
        }
      });
      resColumns.push(resultCol);
    });
    if (!isEqual(this.state.headers, headers)) {
      this.setState({ headers: headers });
    }
    return resColumns;
  };

  onRowSelection = (record, selected, selectedRows) => {
    this.setState({
      selectedRowKeys: (selectedRows || []).map((row) => row.key),
    });
    const { onRowSelect } = this.props;
    onRowSelect && onRowSelect(record, selected, selectedRows);
  };

  onChange = (selectedRowKeys, selectedRows) => {
    this.setState({ selectedRowKeys: selectedRowKeys });
    const { onRowChange } = this.props;
    onRowChange && onRowChange(selectedRowKeys, selectedRows);
  };

  onAllRowsSelection = (selected, selectedRows, changeRows) => {
    this.setState({
      selectedRowKeys: (selectedRows || []).map((row) => row.key),
    });
    const { onAllRowSelect } = this.props;
    onAllRowSelect && onAllRowSelect(selected, selectedRows, changeRows);
  };

  getTableProps = () => {
    const { onRowSelect, filterEnabled, emptyText, components } = this.props;
    const { selectedRowKeys } = this.state;
    let options = {};
    if (onRowSelect) {
      options.rowSelection = {
        selectedRowKeys,
        onChange: this.onChange,
        onSelect: this.onRowSelection,
        onSelectAll: this.onAllRowsSelection,
      };
    }
    options.sortDirections = ['ascend', 'descend'];
    if (filterEnabled) {
      options.onChange = (pagination, filters, sorter, extra) => {
        this.setState({ filteredData: extra.currentDataSource });
      };
    }
    if (trim(emptyText)) {
      options.locale = {
        emptyText: emptyText,
      };
    }
    if (components) {
      options.components = { ...components };
    }
    return options;
  };

  handleSearch = (key, selectedKeys, confirm) => {
    confirm();
    this.updateSearchKey(key, selectedKeys[0]);
  };

  updateSearchKey = (key, searchText) => {
    if (this.state.searchText[key] !== searchText) {
      const nextSearchText = {
        ...this.state.searchText,
        [key]: searchText,
      };
      this.setState({ searchText: nextSearchText });
    }
  };

  handleReset = (key, clearFilters) => {
    clearFilters();
    this.updateSearchKey(key, '');
  };

  getExportButton = () => {
    const { commonMessages, data } = this.props;
    const menu = (
      <Menu>
        <Menu.Item key={0}>
          <CSVLink data={data} headers={this.state.headers}>
            {commonMessages.export_all}
          </CSVLink>
        </Menu.Item>
        <Menu.Item key={1}>
          <CSVLink data={this.state.filteredData} headers={this.state.headers}>
            {commonMessages.export_filtered}
          </CSVLink>
        </Menu.Item>
      </Menu>
    );
    return (
      <Dropdown className="export-button" trigger={['click']} overlay={menu}>
        <Button>
          <DownloadOutlined />
          {commonMessages.export}
        </Button>
      </Dropdown>
    );
  };
  render() {
    const {
      data,
      isCSVExport,
      pagination,
      onRowClick,
      bordered,
      scroll,
      rowSelection,
      expandable,
      summary,
      selectedYear,
      messages,
      isFromSeedCare,
      showGrowerMaxOut,
    } = this.props;
    const { currentCropSalesYear } = this.state;
    const shouldShowGrowerMaxOut =
      showGrowerMaxOut &&
      isCurrentCropSalesYear(selectedYear, currentCropSalesYear);
    const options = this.getTableProps();
    const columns = this.state.formColumns;
    return (
      <div className="custom-table-cont">
        <Row gutter={[0, 16]} align="middle">
          <Col style={{ marginLeft: 'auto' }}>
            {isCSVExport && this.getExportButton()}
          </Col>
        </Row>

        <Row>
          {shouldShowGrowerMaxOut && (
            <Col span={24} style={{ marginBottom: 10 }}>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <Tooltip title={messages?.grower_maxed_out_tooltip}>
                  <WarningFilled className="warning-yellow" />
                </Tooltip>
                <span>{messages?.grower_maxed_out_their_extended_terms}</span>
              </span>
            </Col>
          )}
        </Row>

        <Table
          columns={columns && columns}
          dataSource={data}
          size="small"
          pagination={pagination ? pagination : false}
          bordered={bordered || false}
          scroll={scroll}
          rowSelection={rowSelection}
          expandable={expandable}
          summary={summary}
          onRow={(record, index) => ({
            onClick: () => {
              onRowClick && onRowClick(record);
            },
            style: {
              ...(isFromSeedCare && index === data.length - 1
                ? {
                    backgroundColor: '#d7d7d7',
                    position: 'sticky',
                    bottom: 0,
                    color: 'black',
                    pointerEvents: 'none',
                  }
                : {}),
            },
          })}
          {...options}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, { commonMessages }) => ({
  locale: makeSelectLocale(state),
  commonMessages: {
    export: commonMessages.buttons.export,
    export_all: commonMessages.buttons.export_all,
    export_filtered: commonMessages.buttons.export_filtered,
  },
});

export default ApiHOC()(MessageHOC()(connect(mapStateToProps)(CustomTable)));
